import { useFrame, useThree } from '@react-three/fiber'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CustoControls from '../../components/controls'
import CustoCamera from '../../components/custom-camera'
import { folder, useControls } from 'leva'

import Earth from '../earth'
import Cloud from '../cloud'
import Links from '../links'

import { useHelper } from '@react-three/drei'
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  PerspectiveCamera,
  PointsMaterial,
  SpotLightHelper,
  Vector2,
  Vector3,
} from 'three'
import { useIsDesktop } from '../utils/useIsDesktop'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'

const spotlightConfig: any = {
  showHelper: { value: false },
  intensity: { value: 0.9, min: 0, max: 3 },
  color: { value: '#ffffff' },
  position: { value: [-26, 11, -11] },
  angle: { value: 0.73, min: 0, max: 1 },
  castShadow: { value: false },
  penumbra: { value: 0.85, min: 0, max: 1 },
  distance: { value: 58, min: 0, max: 300 },
  decay: { value: 1, min: 0, max: 2 },
}

const Scene = function (props: any) {
  const earthGrounpRef = useRef<THREE.Group>(null)
  const spotLightRef = useRef<any>(null)
  const isDesktop = useIsDesktop()
  const copyTimeoutRef = useRef<number | null>(null)
  const [controlsTarget, setControlsTarget] = useState<[number, number, number]>([0, 0, 0])
  const { offsetX } = useControls({
    layout: folder({
      offsetX: { value: 0, min: -100, max: 100, step: 0.1 },
    }),
  })

  //@ts-ignore
  const spotlightProps = useControls({
    spotLight: folder(spotlightConfig),
  })

  const { gl, scene, camera, size, viewport } = useThree()

  // ==========================================
  // 💡 核心修复：直接在渲染函数中计算属性，抛弃 useEffect 操作 DOM 的方式
  // ==========================================
  const desktopScale = 0.3
  const mobileScale = 2.0 // 👈 你现在可以随意修改，保存立即生效，且首次加载绝对不会出错！

  const targetDiameter = viewport.width * (isDesktop ? desktopScale : mobileScale)
  const baseDiameter = 7.5
  const currentScale = targetDiameter / baseDiameter
  
  const currentPosition: [number, number, number] = [isDesktop ? offsetX : 0, 0, 0]
  // 无论电脑还是手机，一上来都转到 2.0 弧度，直视大连线！
  const currentRotation: [number, number, number] = [0, 2.0, 0]
  // ==========================================

  // 控制器焦点依然保持在中心
  useEffect(() => {
    setControlsTarget([0, 0, 0])
  }, [])

  useHelper(spotLightRef, spotlightProps.showHelper ? SpotLightHelper : null)

  const composer = useMemo(() => {
    const renderPass = new RenderPass(scene, camera)
    const bloomPass = new UnrealBloomPass(
      new Vector2(size.width, size.height),
      0.7,
      0.9,
      0.2
    )
    const composerInstance = new EffectComposer(gl)
    composerInstance.addPass(renderPass)
    composerInstance.addPass(bloomPass)
    return composerInstance
  }, [gl, scene, camera, size.width, size.height])

  useEffect(() => {
    composer.setSize(size.width, size.height)
  }, [composer, size.width, size.height])

  const starfield = useMemo(() => {
    const makeLayer = (count: number) => {
      const positions = new Float32Array(count * 3)
      for (let i = 0; i < count; i += 1) {
        const radius = 180 + Math.random() * 140
        const theta = Math.acos(2 * Math.random() - 1)
        const phi = Math.random() * Math.PI * 2
        const sinTheta = Math.sin(theta)
        positions[i * 3] = radius * sinTheta * Math.cos(phi)
        positions[i * 3 + 1] = radius * Math.cos(theta)
        positions[i * 3 + 2] = radius * sinTheta * Math.sin(phi)
      }
      const geometry = new BufferGeometry()
      geometry.setAttribute('position', new BufferAttribute(positions, 3))
      return geometry
    }

    return {
      small: makeLayer(1400),
      medium: makeLayer(500),
      large: makeLayer(200),
    }
  }, [])

  const starSmallRef = useRef<PointsMaterial>(null)
  const starMediumRef = useRef<PointsMaterial>(null)
  const starLargeRef = useRef<PointsMaterial>(null)

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    if (starSmallRef.current) {
      starSmallRef.current.opacity = 0.55 + Math.sin(t * 1.2) * 0.15
    }
    if (starMediumRef.current) {
      starMediumRef.current.opacity = 0.65 + Math.sin(t * 1.6 + 1.2) * 0.2
    }
    if (starLargeRef.current) {
      starLargeRef.current.opacity = 0.75 + Math.sin(t * 2.1 + 2.4) * 0.25
    }
  })

  useFrame(() => {
    composer.render()
  }, 1)

  useFrame((state, delta) => {
    if (earthGrounpRef.current) {
      earthGrounpRef.current.rotation.y += 0.0016667
    }
  })

  const handleControlsChange = useCallback(() => {
    if (!earthGrounpRef.current) return
    if (copyTimeoutRef.current) {
      window.clearTimeout(copyTimeoutRef.current)
    }
    copyTimeoutRef.current = window.setTimeout(() => {
      if (!earthGrounpRef.current) return
      const earth = earthGrounpRef.current
      const fmt = (value: number) => Number(value.toFixed(4))
      const payload = {
        earthPosition: [fmt(earth.position.x), fmt(earth.position.y), fmt(earth.position.z)],
        earthRotation: [fmt(earth.rotation.x), fmt(earth.rotation.y), fmt(earth.rotation.z)],
        earthScale: [fmt(earth.scale.x), fmt(earth.scale.y), fmt(earth.scale.z)],
        cameraPosition: [fmt(camera.position.x), fmt(camera.position.y), fmt(camera.position.z)],
        controlsTarget,
        isDesktop,
      }
      const text = JSON.stringify(payload, null, 2)
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {})
      }
    }, 250)
  }, [camera.position.x, camera.position.y, camera.position.z, controlsTarget, isDesktop])

  return (
    <>
      <CustoCamera
        position={isDesktop ? [0, 1.5, 12] : [0, 0, 28]}
        fov={40}
        near={0.1}
        far={1000}
        target={controlsTarget}
        name={'per-camera'}
      >
      </CustoCamera>
      <spotLight ref={spotLightRef} {...spotlightProps} position={[-32, 18, 22]}></spotLight>
      <ambientLight intensity={0.35} color={'#2b303a'}></ambientLight>
      <hemisphereLight intensity={0.3} color={new Color('#172a4a')} groundColor={new Color('#0b0d12')} />
      <directionalLight intensity={0.22} color={new Color('#9bbcff')} position={[45, 10, 20]} />
      <directionalLight intensity={0.18} color={new Color('#7fa8ff')} position={[-45, -5, -20]} />
      <Suspense fallback={null}>
        {/* 💡 核心修复：直接在这里把算好的 scale 和 position 传给 group，完美避开异步加载的时间差！ */}
        <group 
          ref={earthGrounpRef} 
          position={currentPosition} 
          rotation={currentRotation} 
          scale={currentScale}
        >
          <Earth postion={[0, 0, 0]} />
          <Cloud />
          <Links />
        </group>
        <points geometry={starfield.small}>
          <pointsMaterial ref={starSmallRef} size={0.5} color={new Color('#b7d9ff')} transparent opacity={0.65} depthWrite={false} sizeAttenuation />
        </points>
        <points geometry={starfield.medium}>
          <pointsMaterial ref={starMediumRef} size={1.0} color={new Color('#c6e3ff')} transparent opacity={0.75} depthWrite={false} sizeAttenuation />
        </points>
        <points geometry={starfield.large}>
          <pointsMaterial ref={starLargeRef} size={1.8} color={new Color('#e3f1ff')} transparent opacity={0.85} depthWrite={false} sizeAttenuation />
        </points>
      </Suspense>
      <CustoControls
        enableZoom={true}
        enablePan={!isDesktop}
        screenSpacePanning={false}
        autoRotate={false}
        target={controlsTarget}
        onChange={handleControlsChange}
      />
    </>
  )
}

export default Scene